import React, { useState, useEffect, useRef } from 'react';
import './TableBuilder.css';

const TableBuilder = ({ initialData, onDataChange, onClose }) => {
  const [rows, setRows] = useState(0);
  const [columns, setColumns] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [selectedCells, setSelectedCells] = useState([]);
  const [colspan, setColspan] = useState(1);
  const [rowspan, setRowspan] = useState(1);
  const tableRef = useRef(null);

  // Close component when clicking outside of it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tableRef.current && !tableRef.current.contains(event.target)) {
        if (onClose) {
          onClose();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  useEffect(() => {
    if (initialData && initialData.headers && initialData.data) {
      const headers = initialData.headers.split(',');
      const data = initialData.data.split(';').map(row => row.split(','));
      setTableData([headers, ...data]);
      setRows(data.length + 1);
      setColumns(headers.length);
    }
  }, [initialData]);

  const initializeTable = () => {
    const newTable = Array.from({ length: rows }, () => Array(columns).fill(''));
    setTableData(newTable);
  };

  const handleCellChange = (rowIndex, colIndex, value) => {
    const updatedTable = [...tableData];
    if (typeof updatedTable[rowIndex][colIndex] === 'object') {
      // If the cell contains colspan/rowspan, retain those properties
      updatedTable[rowIndex][colIndex] = { ...updatedTable[rowIndex][colIndex], value };
    } else {
      updatedTable[rowIndex][colIndex] = value;
    }
    setTableData(updatedTable);
  };

  const handleCellClick = (rowIndex, colIndex) => {
    const cellKey = `${rowIndex},${colIndex}`;
    setSelectedCells(prev => 
      prev.includes(cellKey) 
        ? prev.filter(cell => cell !== cellKey)
        : [...prev, cellKey]
    );
  };

  const applyCellSpan = () => {
    if (selectedCells.length === 0) return;

    const [startRow, startCol] = selectedCells[0].split(',').map(Number);
    const updatedTable = [...tableData];
    
    for (let i = startRow; i < startRow + rowspan; i++) {
      for (let j = startCol; j < startCol + colspan; j++) {
        if (i === startRow && j === startCol) {
          updatedTable[i][j] = {
            value: updatedTable[i][j].value || updatedTable[i][j], // Retain the original value if it's an object
            colspan,
            rowspan
          };
        } else {
          updatedTable[i][j] = null; // Nullify merged cells
        }
      }
    }

    setTableData(updatedTable);
    setSelectedCells([]);
    setColspan(1);
    setRowspan(1);
  };

  const renderTable = () => {
    if (rows > 0 && columns > 0 && tableData.length > 0) {
      return (
        <table className="table">
          <thead>
            <tr>
              {tableData[0].map((headerCell, colIndex) => (
                <th key={colIndex} className="table-th">
                  <input
                    type="text"
                    value={typeof headerCell === 'object' ? headerCell.value : headerCell}
                    onChange={(e) => handleCellChange(0, colIndex, e.target.value)}
                    className="table-input"
                    placeholder={`Header ${colIndex + 1}`}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableData.slice(1).map((row, rowIndex) => (
              <tr key={rowIndex + 1}>
                {row.map((cell, colIndex) => {
                  if (cell === null) return null; // Skip merged cells
                  const isSelected = selectedCells.includes(`${rowIndex + 1},${colIndex}`);
                  return (
                    <td
                      key={colIndex}
                      className={`table-td ${isSelected ? 'selected-cell' : ''}`}
                      colSpan={cell.colspan || 1}
                      rowSpan={cell.rowspan || 1}
                      onClick={() => handleCellClick(rowIndex + 1, colIndex)}
                    >
                      <input
                        type="text"
                        value={typeof cell === 'object' ? cell.value : cell} // Handle object case
                        onChange={(e) => handleCellChange(rowIndex + 1, colIndex, e.target.value)}
                        className="table-input"
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    return null;
  };

  const handleSave = () => {
    if (onDataChange) {
      onDataChange(tableData);
    }
  };

  return (
    <div className="table-container" ref={tableRef}>
      <h4 className="table-heading">Configure Table</h4>

      <div className="input-group">
        <label className="input-label">Number of Rows</label>
        <input
          type="number"
          value={rows}
          onChange={(e) => setRows(Number(e.target.value))}
          className="input-field"
        />
      </div>

      <div className="input-group">
        <label className="input-label">Number of Columns</label>
        <input
          type="number"
          value={columns}
          onChange={(e) => setColumns(Number(e.target.value))}
          className="input-field"
        />
      </div>

      <button className="initialize-button" onClick={initializeTable}>
        Generate Table
      </button>

      <div className="input-group">
        <label className="input-label">Colspan</label>
        <select
          value={colspan}
          onChange={(e) => setColspan(Number(e.target.value))}
          className="input-field"
        >
          {[...Array(5)].map((_, i) => (
            <option key={i} value={i + 1}>{i + 1}</option>
          ))}
        </select>
      </div>

      <div className="input-group">
        <label className="input-label">Rowspan</label>
        <select
          value={rowspan}
          onChange={(e) => setRowspan(Number(e.target.value))}
          className="input-field"
        >
          {[...Array(5)].map((_, i) => (
            <option key={i} value={i + 1}>{i + 1}</option>
          ))}
        </select>
      </div>

      <button className="initialize-button" onClick={applyCellSpan}>
        Apply Span
      </button>

      <h4 className="table-heading">Editable Table</h4>
      {renderTable()}

      <button className="initialize-button" onClick={handleSave}>
        Save Table Data
      </button>
    </div>
  );
};

export default TableBuilder;
